import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Card, Select, Space, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { epcEstimationService } from '@/services/epcEstimation.service';
import { formatMoney, formatPercent } from '@/utils/format';
import {
  EST3_REVISION_DELTA_FIELDS,
  est3MaterialityColor,
} from './est3GovernanceKinds';

const { Text } = Typography;

export interface EpcEstimateRevisionComparePanelProps {
  estimateId: string;
  revisions: Array<{
    id: string;
    revision_label?: string;
    revision_number?: number;
    status?: string;
  }>;
  defaultToRevisionId?: string | null;
}

/**
 * EST3 UX2 Wave 8 — Revision compare UX (REUSE revisionCompare API).
 */
export const EpcEstimateRevisionComparePanel: React.FC<EpcEstimateRevisionComparePanelProps> = ({
  estimateId,
  revisions,
  defaultToRevisionId,
}) => {
  const sorted = useMemo(
    () => [...revisions].sort((a, b) => Number(b.revision_number) - Number(a.revision_number)),
    [revisions]
  );

  const defaultTo = defaultToRevisionId || sorted[0]?.id || null;
  const defaultFrom = useMemo(() => {
    if (!defaultTo) return null;
    const idx = sorted.findIndex((r) => r.id === defaultTo);
    return sorted[idx + 1]?.id || sorted.find((r) => r.id !== defaultTo)?.id || null;
  }, [sorted, defaultTo]);

  const [fromId, setFromId] = useState<string | null>(defaultFrom);
  const [toId, setToId] = useState<string | null>(defaultTo);

  useEffect(() => {
    setToId(defaultTo);
    setFromId(defaultFrom);
  }, [defaultTo, defaultFrom]);

  const compareQ = useQuery({
    queryKey: ['epc-est-rev-compare', estimateId, fromId, toId],
    queryFn: () => epcEstimationService.revisionCompare(estimateId, fromId!, toId!),
    enabled: sorted.length >= 2 && !!estimateId && !!fromId && !!toId && fromId !== toId,
  });

  if (sorted.length < 2) {
    return (
      <Card size="small" title="Revision compare">
        <Alert type="info" showIcon message="Create a second revision to enable compare." />
      </Card>
    );
  }

  const revOptions = sorted.map((r) => ({
    value: r.id,
    label: `${r.revision_label || r.id} · ${r.status || '—'}`,
  }));

  const deltas = compareQ.data?.deltas || {};
  const materiality = compareQ.data?.materiality;
  const deltaRows = EST3_REVISION_DELTA_FIELDS.map((f) => ({
    key: f.key,
    label: f.label,
    delta: deltas[f.key],
    money: f.money,
  })).filter((r) => r.delta != null && r.delta !== 0);

  return (
    <Card size="small" title="Revision compare">
      <Space wrap style={{ marginBottom: 12 }}>
        <Text>From</Text>
        <Select
          style={{ minWidth: 200 }}
          value={fromId || undefined}
          onChange={setFromId}
          options={revOptions}
          placeholder="Prior revision"
        />
        <Text>→ To</Text>
        <Select
          style={{ minWidth: 200 }}
          value={toId || undefined}
          onChange={setToId}
          options={revOptions}
          placeholder="Later revision"
        />
      </Space>

      {fromId === toId ? (
        <Alert type="warning" showIcon message="Select two different revisions to compare." />
      ) : null}

      {compareQ.isLoading ? <Text type="secondary">Loading compare…</Text> : null}

      {materiality ? (
        <Space wrap style={{ marginBottom: 12 }}>
          <Tag color={est3MaterialityColor(String(materiality.classification))}>
            {String(materiality.classification || '—')}
          </Tag>
          {(materiality.categories || []).map((c: string) => (
            <Tag key={c}>{c}</Tag>
          ))}
        </Space>
      ) : null}

      {deltaRows.length > 0 ? (
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={deltaRows}
          columns={[
            { title: 'Field', dataIndex: 'label' },
            {
              title: 'Δ',
              dataIndex: 'delta',
              width: 140,
              render: (v: number, row: any) =>
                row.money
                  ? formatMoney(v, { display: 'plain', maximumFractionDigits: 0 })
                  : row.key === 'margin_actual'
                    ? formatPercent(v, { maximumFractionDigits: 2 })
                    : v,
            },
          ]}
        />
      ) : compareQ.data ? (
        <Text type="secondary">No material deltas between selected revisions.</Text>
      ) : null}

      {(materiality?.summary_drivers || []).length > 0 ? (
        <Card type="inner" size="small" title="Summary drivers" style={{ marginTop: 12 }}>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {(materiality.summary_drivers as any[]).slice(0, 8).map((d, i) => (
              <li key={i}>
                {d.field}: {String(d.from)} → {String(d.to)}
                {d.delta != null ? ` (Δ ${d.delta})` : ''}
              </li>
            ))}
          </ul>
        </Card>
      ) : null}
    </Card>
  );
};
